// Landmark matching constants (spec 08-landmarks LMK-4).
//
// Client copy for web/mobile (the mobile on-device validation must use the
// same numbers as the server). DUPLICATED in
// packages/db/src/matching/geospatial-matcher.ts — @atlas/db owns the server
// side and cannot be imported here. Keep both copies in sync: divergence
// would let a photo "pass" locally but a walk-past not auto-match (or
// vice-versa), which reads as a bug.

export type LandmarkCategory =
  | "trig_point"
  | "waterfall"
  | "bothy"
  | "shelter"
  | "viewpoint"
  | "mountain_hut"
  | "stone_circle"
  | "bridge"
  | "cave";

// How close (metres) an activity track must pass for an auto-match. Tight for
// things you touch (trig pillars), generous for things you admire from a
// distance (viewpoints, waterfalls).
export const LANDMARK_MATCH_RADIUS_M: Record<LandmarkCategory, number> = {
  trig_point: 50,
  bothy: 60,
  mountain_hut: 60,
  shelter: 60,
  stone_circle: 80,
  bridge: 80,
  cave: 100,
  waterfall: 120,
  viewpoint: 150,
};

export const LANDMARK_MATCH_RADIUS_DEFAULT_M = 50;

// Photo check-ins warn (never block) beyond 3× the match radius — EXIF/phone
// GPS is coarse, so this flags "wrong valley", not "wrong metre".
export const LANDMARK_CHECKIN_WARN_MULTIPLIER = 3;

// Summit check-ins (spec 34): a photo collects a peak only when taken within
// this distance of the summit, with no warn-and-allow. DUPLICATED from
// PEAK_MATCH_RADIUS_M in packages/db/src/matching/geospatial-matcher.ts (the
// activity matcher's peak radius) - keep both in sync.
export const PEAK_CHECKIN_RADIUS_M = 75;

export function landmarkCheckinWarnRadiusM(category: string): number {
  const base =
    LANDMARK_MATCH_RADIUS_M[category as LandmarkCategory] ?? LANDMARK_MATCH_RADIUS_DEFAULT_M;
  return base * LANDMARK_CHECKIN_WARN_MULTIPLIER;
}

/** Great-circle distance in metres — mobile's offline stand-in for ST_Distance. */
export function haversineDistanceM(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
